import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { tokens } from '../../../theme/tokens';

export const v2CardSx = {
  bgcolor: tokens.surface.card,
  border: '1px solid',
  borderColor: tokens.line.default,
  borderRadius: `${tokens.radius.panel}px`,
  boxShadow: tokens.shadow.card,
} as const;

export const v2Mono = { fontFamily: tokens.font.mono, fontSize: 12 } as const;

export const V2_TAXONOMY_QUERY_KEY = ['merchant-taxonomy'] as const;

/** The 11px uppercase label the handoff puts over a group. */
export const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="span"
    sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}
  >
    {children}
  </Typography>
);

/** Numbered circle in front of a step (22px; 20px in the section menu); step 4 is the amber one. */
export const StepCircle: React.FC<{ n: number; tone?: 'info' | 'caution'; size?: number }> = ({ n, tone, size = 22 }) => {
  const signal = tokens.signal[tone ?? (n === 4 ? 'caution' : 'info')];
  return (
    <Box
      component="span"
      sx={{
        flex: '0 0 auto',
        display: 'grid',
        placeItems: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: signal.tint,
        color: signal.ink,
        fontSize: size < 22 ? 11 : 11.5,
        fontWeight: 700,
      }}
    >
      {n}
    </Box>
  );
};

/** Text-only action, the "link" button the handoff uses for Replace, Add rule, Show advanced settings. */
export const LinkAction: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  tone?: 'accent' | 'muted';
  disabled?: boolean;
  startIcon?: React.ReactNode;
  'aria-label'?: string;
}> = ({ children, onClick, tone = 'accent', disabled, startIcon, ...rest }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    aria-label={rest['aria-label']}
    sx={{
      gap: 0.5,
      fontSize: 13,
      fontWeight: 600,
      color: tone === 'accent' ? tokens.accent.main : tokens.ink.muted,
      '&:hover': { color: tone === 'accent' ? tokens.accent.hover : tokens.ink.primary },
      '&.Mui-disabled': { opacity: 0.5 },
    }}
  >
    {startIcon}
    {children}
  </ButtonBase>
);

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** Right-aligned count in a muted (or toned) colour, as on the category map filters. */
  count?: number;
  countTone?: 'caution' | 'info';
}

interface SegmentedProps<T extends string> {
  options: Array<SegmentOption<T>>;
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  /** `radio` for a one-of-three choice that is saved; `filter` for a view filter. */
  kind?: 'radio' | 'filter';
}

/** The small segmented control used for field owners and the category map filters. */
export function SegmentedControl<T extends string>({ options, value, onChange, kind = 'radio', ...rest }: SegmentedProps<T>) {
  return (
    <Box
      role={kind === 'radio' ? 'radiogroup' : 'group'}
      aria-label={rest['aria-label']}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px', p: '3px', bgcolor: tokens.surface.tint, borderRadius: '9px' }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            role={kind === 'radio' ? 'radio' : undefined}
            aria-checked={kind === 'radio' ? active : undefined}
            aria-pressed={kind === 'filter' ? active : undefined}
            onClick={() => onChange(option.value)}
            sx={{
              height: 28,
              px: kind === 'radio' ? 1.25 : 1.375,
              gap: 0.75,
              borderRadius: '7px',
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: active ? tokens.ink.primary : tokens.ink.muted,
              bgcolor: active ? tokens.surface.card : 'transparent',
              boxShadow: active ? tokens.shadow.card : 'none',
            }}
          >
            {option.label}
            {option.count !== undefined && (
              <Box
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontVariantNumeric: 'tabular-nums',
                  color: option.countTone === 'caution'
                    ? tokens.signal.caution.ink
                    : option.countTone === 'info' ? tokens.accent.main : tokens.ink.faint,
                }}
              >
                {option.count}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/** Text for screen readers only: what a dot or badge says by colour alone. */
export const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
